import classNames from 'classnames'
import { useRef, useState, useEffect, ReactNode } from 'react'

type ScrollableListProps = {
  children: ReactNode
  maxHeight?: number
  minHeight?: number
  className?: string
  /** Fill the parent's available height (flex) instead of using a fixed pixel height. */
  fill?: boolean
  /** Class applied to the inner flex column that wraps the children (controls gap). */
  innerClassName?: string
}

export function ScrollableList({
  children,
  maxHeight = 340,
  minHeight,
  className = ``,
  fill = false,
  innerClassName = `gap-1`,
}: ScrollableListProps) {
  const wrapperRef = useRef<HTMLDivElement>(null)
  const [isAtTop, setIsAtTop] = useState(true)
  const [hasOverflow, setHasOverflow] = useState(false)

  useEffect(() => {
    const wrapper = wrapperRef.current
    if (!wrapper) return

    const checkOverflow = () => {
      setHasOverflow(wrapper.scrollHeight > wrapper.clientHeight)
    }

    const handleScroll = () => {
      const { scrollTop, scrollHeight, clientHeight } = wrapper
      const atTop = scrollTop < 20
      const atBottom = scrollTop + clientHeight >= scrollHeight - 20
      setIsAtTop(atTop || !atBottom)
    }

    checkOverflow()
    wrapper.addEventListener(`scroll`, handleScroll)

    const observer = new ResizeObserver(checkOverflow)
    observer.observe(wrapper)

    return () => {
      wrapper.removeEventListener(`scroll`, handleScroll)
      observer.disconnect()
    }
  }, [children])

  const handleButtonClick = () => {
    const wrapper = wrapperRef.current
    if (!wrapper) return

    if (isAtTop) {
      wrapper.scrollTo({ top: wrapper.scrollHeight, behavior: `smooth` })
    } else {
      wrapper.scrollTo({ top: 0, behavior: `smooth` })
    }
  }

  return (
    <div className={classNames(fill && `flex min-h-0 flex-1 flex-col`, className)}>
      <div
        ref={wrapperRef}
        className={classNames(`scrollbar-hide relative overflow-y-auto`, fill && `min-h-0 flex-1`)}
        style={fill ? undefined : { maxHeight: `${maxHeight}px`, minHeight: minHeight ? `${minHeight}px` : undefined }}
      >
        {/* Padding matching the fade's height, so the last row scrolls clear of it. */}
        <div className={classNames(`flex flex-col`, innerClassName, hasOverflow && `pb-12`)}>{children}</div>

        {hasOverflow && (
          <div className="pointer-events-none sticky bottom-0 left-0 right-0 z-30 -mt-12 h-12 bg-gradient-to-b from-transparent to-(--surface)" />
        )}
      </div>

      {hasOverflow && (
        <button
          onClick={handleButtonClick}
          className="mx-auto mt-1 flex w-fit items-center gap-1 rounded-md border border-default bg-surface px-2.5 py-1 text-xs font-medium text-subtle transition-all hover:border-blue-500 hover:text-blue-500"
        >
          <span>{isAtTop ? `See more` : `Back to top`}</span>
          <svg
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            className={`h-3 w-3 transition-transform ${!isAtTop ? `rotate-180` : ``}`}
          >
            <path d="M19 9l-7 7-7-7" />
          </svg>
        </button>
      )}
    </div>
  )
}
